import React, { useCallback, useEffect, useRef, useState } from "react"

import { cn, ResizeGrip, resizeHandleClassName } from "@coval/ui"

import type { DashboardWidget } from "../../types/core"
import {
  getRequestedWidgetWidth,
  getWidgetWidthOptions,
  snapWidgetWidth,
} from "../../utils/dashboardWidgetSizing"

interface WidgetResizeHandleProps {
  widget: DashboardWidget
  columnWidthPx: number
  /** Distance from the grid cell's edges to the card's border. */
  edgeInsetPx: number
  /** Reflows the grid mid-drag without saving. */
  onPreview: (columns: number) => void
  onCommit: (columns: number) => void
  onCancel: () => void
}

interface ResizeDrag {
  pointerId: number
  startX: number
  startColumns: number
}

/**
 * Drags a widget's right edge to change its width, snapping to the widths that
 * widget is allowed. There is no vertical counterpart: heights come from the
 * widget type.
 *
 * The drag is hand-rolled rather than delegated to a resize library because the
 * unit here is grid columns from a non-uniform set, not pixels: resize
 * libraries take a pixel width and a single uniform step, and a resize here can
 * also pull neighbouring widgets into or out of the row.
 *
 * Every width is measured from the one the widget asked for, never from the one
 * it renders at. A row with columns to spare grows its widgets past their
 * requested widths, and stepping from a grown width would round the request
 * away — leaving the narrower options unreachable however far the handle is
 * dragged.
 */
export const WidgetResizeHandle: React.FC<WidgetResizeHandleProps> = ({
  widget,
  columnWidthPx,
  edgeInsetPx,
  onPreview,
  onCommit,
  onCancel,
}) => {
  const dragRef = useRef<ResizeDrag | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const options = getWidgetWidthOptions(widget)
  const currentColumns = getRequestedWidgetWidth(widget)
  const currentOption = options.find(
    (option) => option.columns === currentColumns,
  )

  const endDrag = useCallback(() => {
    dragRef.current = null
    setIsDragging(false)
  }, [])

  const columnsAt = useCallback(
    (clientX: number, drag: ResizeDrag) => {
      const draggedColumns = Math.round((clientX - drag.startX) / columnWidthPx)
      // A pointer event without usable coordinates must leave the width alone,
      // not fall through to the widget type's default.
      const columns = Number.isFinite(draggedColumns)
        ? drag.startColumns + draggedColumns
        : drag.startColumns
      return snapWidgetWidth(widget, columns)
    },
    [widget, columnWidthPx],
  )

  /** A drag in flight outlives the handle's focus, so Escape is caught globally. */
  useEffect(() => {
    if (!isDragging) {
      return
    }

    const cancelOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        endDrag()
        onCancel()
      }
    }

    window.addEventListener("keydown", cancelOnEscape)
    return () => window.removeEventListener("keydown", cancelOnEscape)
  }, [isDragging, endDrag, onCancel])

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // A second finger mid-drag must not restart the gesture from a new origin.
      if (columnWidthPx <= 0 || dragRef.current) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startColumns: currentColumns,
      }
      setIsDragging(true)
      event.currentTarget.setPointerCapture(event.pointerId)
    },
    [columnWidthPx, currentColumns],
  )

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (drag?.pointerId === event.pointerId) {
        onPreview(columnsAt(event.clientX, drag))
      }
    },
    [columnsAt, onPreview],
  )

  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current
      if (drag && drag.pointerId !== event.pointerId) {
        return
      }

      endDrag()
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }

      if (!drag) {
        onCancel()
        return
      }

      onCommit(columnsAt(event.clientX, drag))
    },
    [columnsAt, endDrag, onCommit, onCancel],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const currentIndex = options.findIndex(
        (option) => option.columns === currentColumns,
      )

      const target =
        event.key === "ArrowRight"
          ? options[currentIndex + 1]
          : event.key === "ArrowLeft"
            ? options[currentIndex - 1]
            : event.key === "Home"
              ? options[0]
              : event.key === "End"
                ? options[options.length - 1]
                : undefined

      if (!target) {
        return
      }

      event.preventDefault()
      if (target.columns !== currentColumns) {
        onCommit(target.columns)
      }
    },
    [options, currentColumns, onCommit],
  )

  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label={`Width of ${widget.config?.title || widget.title}`}
      aria-valuemin={options[0]?.columns ?? currentColumns}
      aria-valuemax={options[options.length - 1]?.columns ?? currentColumns}
      aria-valuenow={currentColumns}
      aria-valuetext={currentOption?.label}
      data-testid={`dashboard-widget-${widget.id}-resize`}
      data-resizing={isDragging}
      // Centred on the card's right border, the same divider affordance every
      // other resizable pane uses.
      style={{ top: edgeInsetPx, bottom: edgeInsetPx, right: edgeInsetPx }}
      className={cn(
        resizeHandleClassName,
        "h-auto translate-x-1/2 touch-none focus-visible:outline-none",
      )}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      onKeyDown={handleKeyDown}
    >
      {/* Stops short of the card's rounded corners, where the border curves away. */}
      <ResizeGrip className="inset-y-1" />
    </div>
  )
}
